import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Table,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2SearchInput,
  Icon,
  V2FormField,
  Select,
  Alert,
  StatePanel,
  type V2TableColumn,
  type V2BadgeTone,
  V2FormGrid,
  V2FormActions,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, apiPatch, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  PROJECT_STATUS_OPTIONS,
  type Project,
  type ProjectListResponse,
  type ProjectsOverdueReportResponse,
} from '../../lib/types/projects.js';
import './project-list.css';
import { ProjectTeamPanel } from './ProjectTeamPanel.js';

const PAGE_SIZE = 20;

const STATUS_TONE: Record<string, V2BadgeTone> = {
  برنامه‌ریزی: 'neutral',
  فعال: 'success',
  متوقف: 'warn',
  تکمیل‌شده: 'info',
  'لغو شده': 'danger',
};

interface Row extends Project {
  readonly id: string;
  readonly isOverdue: boolean;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: "Sakhtar Projects & Tasks.dc.html" § SCREEN 02 · PROJECT LIST
 * archetype: ENTITY_LIST — real, paginated `GET .../projects` (search +
 * status supported server-side, per apps/api/src/projects/presentation.ts
 * `parseListQuery`). "سررسید گذشته" highlighting cross-references the same
 * real overdue report the overview page uses (GET .../projects/reports/
 * overdue) rather than inventing a per-row isOverdue flag the list
 * endpoint itself doesn't return — shown the board's way: semantic colour
 * plus a small bell icon, no coloured row. Pagination is the endpoint's
 * real `total/offset/limit` envelope ("نمایش ۱ تا ۷ از ۱۲" + قبلی/بعدی).
 * Below 900px the table is swapped for the board's "PROJECT CARDS ·
 * MOBILE" layout (project-list.css), keeping the same key fields and 44px
 * touch targets.
 * Not rendered, because the list endpoint has no backing field for them
 * (omit rather than fabricate, same precedent as SuppliersPage.tsx): the
 * board's مدیر پروژه / پیشرفت / شروع / کارهای باز columns and the
 * "همهٔ مدیران پروژه", "بازهٔ زمانی" and "فقط پروژه‌های من" filters.
 * state reference: ready · empty · permission_denied · network_error ·
 *   loading (the board's STATE FRAMES skeleton)
 */
export function ProjectListPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  // Initialized from the URL once (?status=…) so a Hub drill link
  // (FE-P0 FE-PRJ-1) actually pre-filters — never re-synced afterward, so
  // the select stays a normal controlled input once the user changes it.
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [offset, setOffset] = useState(0);
  const [teamOf, setTeamOf] = useState<{ ref: string; name: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const query = new URLSearchParams();
  if (search.trim()) query.set('search', search.trim());
  if (status) query.set('status', status);
  query.set('offset', String(offset));
  query.set('limit', String(PAGE_SIZE));
  const qs = query.toString();

  const projects = useApiGet<ProjectListResponse>(
    slug ? `${base}/api/v1/projects${qs ? `?${qs}` : ''}` : null,
    [slug, qs, refreshKey],
  );
  const overdueReport = useApiGet<ProjectsOverdueReportResponse>(
    slug ? `${base}/api/v1/projects/reports/overdue` : null,
    [slug, refreshKey],
  );

  const overdueRefs = useMemo(() => {
    if (overdueReport.status !== 'ready') return new Set<string>();
    return new Set(overdueReport.data.projects.map((p) => p.ref));
  }, [overdueReport]);

  const rows = useMemo<Row[]>(() => {
    if (projects.status !== 'ready') return [];
    return projects.data.projects.map((p) => ({
      ...p,
      id: p.ref,
      isOverdue: overdueRefs.has(p.ref),
    }));
  }, [projects, overdueRefs]);

  if (!slug) return null;

  const total = projects.status === 'ready' ? (projects.data.total ?? rows.length) : 0;
  const pageStart = rows.length === 0 ? 0 : offset + 1;
  const pageEnd = offset + rows.length;
  const hasFilters = search.trim() !== '' || status !== '';

  function onSearchChange(next: string) {
    setSearch(next);
    setOffset(0);
  }

  function onStatusFilterChange(next: string) {
    setStatus(next);
    setOffset(0);
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/projects`, {
        name: name.trim(),
        dueDate: dueDate || undefined,
      });
      setName('');
      setDueDate('');
      setShowForm(false);
      setRefreshKey((k) => k + 1);
    } catch {
      setFormError('ثبت پروژه ناموفق بود.');
    } finally {
      setSubmitting(false);
    }
  }

  const [updatingRef, setUpdatingRef] = useState<string | null>(null);
  const [statusActionError, setStatusActionError] = useState<string | null>(null);

  const ALLOWED_PROJECT_TRANSITIONS: Record<string, readonly { value: string; label: string }[]> = {
    برنامه‌ریزی: [
      { value: 'active', label: 'فعال' },
      { value: 'cancelled', label: 'لغو شده' },
    ],
    فعال: [
      { value: 'on_hold', label: 'متوقف' },
      { value: 'completed', label: 'تکمیل‌شده' },
      { value: 'cancelled', label: 'لغو شده' },
    ],
    متوقف: [
      { value: 'active', label: 'فعال' },
      { value: 'cancelled', label: 'لغو شده' },
    ],
    تکمیل‌شده: [],
    'لغو شده': [],
  };

  async function onStatusChange(ref: string, nextStatus: string) {
    setStatusActionError(null);
    setUpdatingRef(ref);
    try {
      await apiPatch(`${base}/api/v1/projects/${encodeURIComponent(ref)}`, { status: nextStatus });
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        setStatusActionError(caught.envelope.error.message || 'این تغییر وضعیت مجاز نیست.');
      } else if (caught instanceof ApiError && caught.status === 403) {
        setStatusActionError('شما اجازهٔ تغییر وضعیت این پروژه را ندارید.');
      } else {
        setStatusActionError('تغییر وضعیت ناموفق بود.');
      }
    } finally {
      setUpdatingRef(null);
    }
  }

  const statusBadge = (row: Row) => (
    <V2Badge tone={STATUS_TONE[row.statusLabel] ?? 'neutral'}>{row.statusLabel}</V2Badge>
  );

  const dueCell = (row: Row, overdueSuffix = false): ReactNode =>
    row.isOverdue ? (
      <span
        title="سررسید گذشته"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--prj-danger-text, var(--sk-danger))',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayJalaliDate(row.dueDate)}
        {overdueSuffix ? ' — گذشته' : <Icon name="bell" size="sm" title="سررسید گذشته" />}
      </span>
    ) : (
      <span
        style={{
          color: row.dueDate ? 'var(--sk-fg-muted)' : 'var(--sk-fg-muted)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayJalaliDate(row.dueDate)}
      </span>
    );

  const statusChangeSelect = (row: Row) => {
    const allowed = ALLOWED_PROJECT_TRANSITIONS[row.statusLabel] ?? [];
    return (
      <Select
        aria-label={`تغییر وضعیت ${row.name}`}
        value=""
        disabled={updatingRef === row.ref || allowed.length === 0}
        onChange={(e) => e.target.value && void onStatusChange(row.ref, e.target.value)}
      >
        <option value="">
          {updatingRef === row.ref
            ? 'در حال ثبت…'
            : allowed.length === 0
              ? 'وضعیت نهایی'
              : 'تغییر وضعیت…'}
        </option>
        {allowed.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  };

  const teamButton = (row: Row) => (
    <V2Button
      variant="ghost"
      size="sm"
      icon="users"
      aria-label={`تیم پروژهٔ ${row.name}`}
      onClick={() => setTeamOf({ ref: row.ref, name: row.name })}
    >
      تیم
    </V2Button>
  );

  const columns: readonly V2TableColumn<Row>[] = [
    {
      key: 'name',
      header: 'پروژه',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Link
            to={`${base}/projects/list/${encodeURIComponent(row.ref)}`}
            style={{
              fontWeight: 600,
              color: 'var(--prj-link, var(--sk-brand-600))',
              textDecoration: 'none',
            }}
          >
            {row.name}
          </Link>
          <span
            className="ltr"
            style={{
              fontSize: 12,
              color: 'var(--sk-fg-muted)',
              fontFamily: 'var(--sk-font-mono)',
            }}
          >
            {row.ref}
          </span>
        </div>
      ),
    },
    { key: 'status', header: 'وضعیت', render: statusBadge },
    { key: 'due', header: 'سررسید', render: (row) => dueCell(row) },
    {
      key: 'actions',
      header: 'عملیات',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {statusChangeSelect(row)}
          {teamButton(row)}
        </div>
      ),
    },
  ];

  const emptyState = hasFilters ? (
    <V2EmptyState
      icon="search"
      title="پروژه‌ای با این فیلترها پیدا نشد."
      description="عبارت جستجو یا وضعیت را تغییر دهید."
    />
  ) : (
    <V2EmptyState
      icon="projects"
      title="پروژه‌ای برای نمایش وجود ندارد."
      description="اولین پروژه را بسازید تا کارها و مسئولیت‌ها زیر آن ثبت شود."
      action={
        <V2Button variant="primary" icon="plus" onClick={() => setShowForm(true)}>
          پروژه جدید
        </V2Button>
      }
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <V2Button variant="primary" icon="plus" onClick={() => setShowForm((v) => !v)}>
          پروژه جدید
        </V2Button>
      </div>

      {teamOf ? (
        <ProjectTeamPanel
          key={teamOf.ref}
          slug={slug}
          projectRef={teamOf.ref}
          projectName={teamOf.name}
          onClose={() => setTeamOf(null)}
        />
      ) : null}

      {showForm ? (
        <V2Card>
          <V2CardHeader title="پروژه جدید" />
          <form onSubmit={onCreate} style={{ padding: '4px 20px 20px' }}>
            <V2FormGrid>
              <V2FormField label="نام" required width="wide">
                <input
                  className="sk-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </V2FormField>
              <V2FormField label="سررسید">
                <JalaliDateInput value={dueDate} onChange={setDueDate} />
              </V2FormField>
            </V2FormGrid>
            <V2FormActions>
              <V2Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'در حال ثبت…' : 'ثبت پروژه'}
              </V2Button>
            </V2FormActions>
          </form>
          {formError ? (
            <div style={{ padding: '0 20px 16px' }}>
              <Alert tone="danger" title="خطا">
                {formError}
              </Alert>
            </div>
          ) : null}
        </V2Card>
      ) : null}

      {projects.status === 'loading' ? (
        <V2Card clipped>
          <div aria-busy="true" aria-live="polite">
            <div
              className="prj-list__skeleton-row"
              style={{ background: 'var(--sk-bg-canvas)', padding: '14px 18px' }}
            >
              <div className="sk-skel" style={{ height: 10, flex: 1 }} />
              <div className="sk-skel" style={{ height: 10, width: 60 }} />
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className="prj-list__skeleton-row">
                <div className="sk-skel" style={{ height: 12, flex: 1 }} />
                <div
                  className="sk-skel"
                  style={{ height: 22, width: 58, borderRadius: 'var(--sk-r-pill)' }}
                />
              </div>
            ))}
          </div>
        </V2Card>
      ) : null}
      {projects.status === 'error' ? (
        projects.error.status === 403 ? (
          <StatePanel
            variant="permission_denied"
            title="شما دسترسی لازم برای مشاهده این پروژه‌ها را ندارید."
            description="فهرست پروژه‌ها بر پایهٔ عضویت پروژه و دامنهٔ رکورد شما ساخته می‌شود."
          />
        ) : (
          <StatePanel variant="network_error" title="فهرست قابل نمایش نیست" />
        )
      ) : null}

      {statusActionError ? (
        <Alert tone="danger" title="خطا در تغییر وضعیت">
          {statusActionError}
        </Alert>
      ) : null}

      {projects.status === 'ready' ? (
        <V2Card clipped>
          <div className="prj-list__toolbar">
            <V2SearchInput
              placeholder="نام یا کد پروژه"
              value={search}
              onChange={onSearchChange}
              width={240}
            />
            <Select
              aria-label="وضعیت پروژه"
              value={status}
              onChange={(e) => onStatusFilterChange(e.target.value)}
            >
              <option value="">همهٔ وضعیت‌ها</option>
              {PROJECT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <span className="prj-list__count">
              {total.toLocaleString('fa-IR')} پروژه در دسترس شما
            </span>
          </div>

          <div className="prj-list__table">
            <V2Table columns={columns} rows={rows} zebra emptyState={emptyState} />
          </div>

          <div className="prj-list__cards">
            {rows.length === 0
              ? emptyState
              : rows.map((row) => (
                  <div key={row.id} className="sk-card prj-list__card">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <Link
                        to={`${base}/projects/list/${encodeURIComponent(row.ref)}`}
                        style={{
                          flex: 1,
                          fontSize: 15,
                          fontWeight: 600,
                          color: 'var(--prj-link, var(--sk-brand-600))',
                          lineHeight: 1.6,
                          textDecoration: 'none',
                        }}
                      >
                        {row.name}
                      </Link>
                      {statusBadge(row)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--sk-fg-muted)' }}>کد</span>
                        <span className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                          {row.ref}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--sk-fg-muted)' }}>سررسید</span>
                        {dueCell(row, true)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>{statusChangeSelect(row)}</div>
                      {teamButton(row)}
                    </div>
                  </div>
                ))}
          </div>

          {rows.length > 0 ? (
            <div className="prj-list__footer">
              <span>
                نمایش {pageStart.toLocaleString('fa-IR')} تا {pageEnd.toLocaleString('fa-IR')} از{' '}
                {total.toLocaleString('fa-IR')}
              </span>
              <div className="prj-list__footer-actions">
                <V2Button
                  variant="ghost"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  قبلی
                </V2Button>
                <V2Button
                  variant="secondary"
                  size="sm"
                  disabled={pageEnd >= total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  بعدی
                </V2Button>
              </div>
            </div>
          ) : null}
        </V2Card>
      ) : null}
    </div>
  );
}
